import React, { useCallback, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import StatsAPI from "@/src/api/StatsAPI";

import { projectCountriesQuery } from "./dashboard.queries";
import { buildCountriesMapData } from "./dashboard.utils";
import MapViewToggle from "./MapViewToggle";
import { formatCount } from "../charts/charts.utils";
import CountriesMapQueryCard from "../charts/CountriesMapQueryCard";
import MapTooltipRow from "../charts/MapTooltipRow";

import type { JSX, ReactNode } from "react";

import type { GetProjectCountriesResponse } from "@/src/api/StatsAPI.types";
import type { AppState } from "@/src/reducers";

import type { CountriesViewProps } from "./dashboard.types";

function CountriesMapCard({
  period,
  view,
  onViewChange,
}: CountriesViewProps): JSX.Element {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { t, i18n } = useTranslation("editor-analytics");
  const lang = i18n.language;
  const countries = useSelector(
    (state: AppState) => state.fetchReducer.countries,
  );
  const query = projectCountriesQuery(projectSlug, period);
  const { data } = useQuery(query);

  const statByCountry = useMemo(
    () => buildCountriesMapData(data, countries, view),
    [data, countries, view],
  );
  const buildValueByCountry = useCallback(
    (response: GetProjectCountriesResponse | undefined) =>
      Object.fromEntries(
        Object.entries(buildCountriesMapData(response, countries, view)).map(
          ([code, stat]) => [code, view === "views" ? stat.total : stat.users],
        ),
      ),
    [countries, view],
  );
  const renderExtraTooltipRows = (code: string): ReactNode => {
    const stat = statByCountry[code];
    if (!stat || view !== "views") return null;
    return (
      <React.Fragment>
        <MapTooltipRow
          label={t("charts.series.paid")}
          value={formatCount(stat.paid, lang)}
        />
        <MapTooltipRow
          label={t("charts.series.free")}
          value={formatCount(stat.free, lang)}
        />
      </React.Fragment>
    );
  };
  const fetchCsv = async (): Promise<BlobPart> =>
    StatsAPI.getProjectCountries({
      projectSlug,
      startDate: period.startDate,
      endDate: period.endDate,
      isExport: true,
    });

  return (
    <CountriesMapQueryCard
      buildValueByCountry={buildValueByCountry}
      csvExport={{ fetchCsv, fileName: "countries.csv" }}
      query={query}
      renderExtraTooltipRows={renderExtraTooltipRows}
      title={t("dashboard.charts.countries")}
      valueLabel={view === "views" ? t("charts.series.views") : t("users")}
      extraActions={
        <MapViewToggle
          ariaLabel={t("dashboard.mapView")}
          onChange={onViewChange}
          value={view}
        />
      }
    />
  );
}

export default CountriesMapCard;
